// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useState } from "react"
import { useApiClient } from "@/api/useApiClient"
import { useAuth } from "@/hooks/useAuth"
import { identifyUser } from "@/lib/analytics"
import { getMarketingConsent, onMarketingConsentChange } from "@/lib/consentGate"

/**
 * Ties the signed-in session to PostHog under OUR account id (the /v1/me `id` — NOT the Cognito `sub`, which the
 * API maps to a different internal uuid — and never the email). Renders nothing.
 *
 * It only asks /v1/me while the Produktanalyse consent is granted: a visitor who declined costs no extra
 * request and nothing about them is handed to analytics code. A consent granted later in the session re-runs
 * the effect. Sign-out is handled where the session is cleared (AuthProvider.clearSession -> resetUser).
 * Mounted inside <AuthProvider>.
 */
export function AnalyticsIdentity() {
  const { status } = useAuth()
  const api = useApiClient()
  const [consented, setConsented] = useState(() => getMarketingConsent() === "granted")
  useEffect(() => onMarketingConsentChange(c => setConsented(c === "granted")), [])

  useEffect(() => {
    if (status !== "signedIn" || !consented) return
    let cancelled = false
    api
      .getMe()
      .then(me => {
        if (!cancelled) identifyUser(me.id)
      })
      .catch(() => {
        // Identification is best-effort; the app itself reports /v1/me failures where they matter.
      })
    return () => {
      cancelled = true
    }
  }, [status, consented, api])

  return null
}
